import React, { useMemo } from "react";
import { map } from "lodash";
import styled from "styled-components";
import {
  Color,
  FontSize,
  Spacing,
  ZIndexes,
  background,
  content,
  cover,
  prettyScrollbars,
  transparentize,
} from "@glossy";
import { tr } from "@linkfluence/i18n";
import { Locale } from "@linkfluence/models/locale";
import { User } from "@linkfluence/models/user";
import Button from "@ui-kit/button";
import Footer from "@ui-kit/form/footer";
import Icon from "@ui-kit/icon";
import Tabs from "@ui-kit/tabs";

interface IProps {
  onClose: () => void;
  tab: Tab;
  onLogout: () => void;
  user: User;
  children: React.ReactNode;
  onChangeTab: (tab: Tab) => void;
}

export enum Tab {
  Informations = "informations",
  Customization = "customization",
  Security = "security",
  Access = "access",
}

const getTabLabel = (tab: Tab, locale: Locale): string =>
  ({
    [Tab.Informations]: tr(locale, "PROFILE::MENU::INFO"),
    [Tab.Customization]: tr(locale, "PROFILE::MENU::CUSTOMIZATION"),
    [Tab.Security]: tr(locale, "PROFILE::MENU::PASSWORD"),
    [Tab.Access]: tr(locale, "PROFILE::MENU::ACCESS"),
  }[tab] || "");

const Overlay = styled.div`
  position: absolute;
  ${cover()};
  z-index: ${ZIndexes.Overlay};
  background-color: ${transparentize(0.9, Color.Black)};
`;

const Panel = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  width: 500px;
  z-index: ${ZIndexes.Panel};
  display: flex;
  flex-direction: column;
  background-color: ${background};
  color: ${content};
`;

const Header = styled.div`
  padding: ${Spacing.Large};
  margin-bottom: ${Spacing.Medium};
  font-size: ${FontSize.Large};
  display: flex;
  column-gap: ${Spacing.Medium};
  align-items: center;
  line-height: 1em;
`;

const Close = styled(Icon)`
  margin-left: auto;
  cursor: pointer;
  :hover {
    opacity: 0.5;
  }
`;

const Body = styled.div`
  flex-grow: 1;
  margin-top: ${Spacing.Medium};
  padding: ${Spacing.Large};
  ${prettyScrollbars()};
  display: flex;
  flex-direction: column;
  row-gap: ${Spacing.Medium};
`;

const useTabs = (locale) =>
  useMemo(
    () =>
      map(Tab, (_tab) => ({
        value: _tab,
        label: getTabLabel(_tab, locale),
      })),
    [locale]
  );

const UiKitProfile = ({
  onClose,
  user: { locale },
  onLogout,
  children,
  tab,
  onChangeTab,
}: IProps) => {
  const tabs = useTabs(locale);

  return (
    <>
      <Overlay onClick={onClose} />
      <Panel data-testid="profile-panel">
        <Header>
          <Icon glyph="user-fill" />
          <span>{tr(locale, "PROFILE::TITLE")}</span>
          <Close glyph="cross" onClick={onClose} />
        </Header>
        <Tabs
          tabs={tabs}
          defaultValue={tab}
          onValueChange={(value) => onChangeTab(value as Tab)}
          size="s"
          stretch={false}
        />
        <Body>{children}</Body>
        <Footer>
          <Button
            variant="secondary"
            data-testid="logout-button-in-panel"
            onClick={onLogout}
          >
            {tr(locale, "PROFILE::LOGOUT")}
          </Button>
        </Footer>
      </Panel>
    </>
  );
};

export default UiKitProfile;
